import { intervalToDuration, isPast } from 'date-fns'
import { string } from 'yup'
import { datadogLogs } from '@datadog/browser-logs'

import { LicenceType, OptionItem, OvernightLocation, VehicleSteering } from 'types'
import { DateObject } from 'src/types/date'
import { convertDateObjectIntoDate, convertToUkDate, isCompleteDateObjectType } from 'utils/date'

export const monthsArray = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const generateMonthNumber = (month: string): number | null => {
  if (!month) {
    return null
  }
  const monthIndex = monthsArray.indexOf(month)
  return monthIndex + 1
}

export const generateMonthString = (month: number): string => {
  return monthsArray[month - 1]
}

export const generateMonths: OptionItem[] = [...Array(12).keys()].map((_, i) => {
  return { label: monthsArray[i], value: monthsArray[i] }
})

const generateMonthsUpToCurrentMonth = (): OptionItem[] => {
  const currentMonth = new Date().getMonth() + 1
  return [...Array(currentMonth).keys()].map((_, i) => {
    return { label: monthsArray[i], value: monthsArray[i] }
  })
}

export const generateMonthsListBasedOnYear = (year: string | undefined): OptionItem[] => {
  const currentYear = new Date().getFullYear()
  if (Number(year) === currentYear) {
    return generateMonthsUpToCurrentMonth()
  }
  return generateMonths
}

export const shouldResetMonthsList = (
  year: string | undefined,
  month: string | undefined,
): boolean => {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth()
  if (!year || !month) {
    return false
  }
  if (Number(year) === currentYear && monthsArray.indexOf(month) > currentMonth) {
    return true
  }
  return false
}

export const generateYearValues = (limit = 20): OptionItem[] =>
  Array.from({ length: limit + 1 }, (_, i) => ({
    label: String(`${i} years`),
    value: String(i),
  }))

export const generateYears = (limit = 80) =>
  Array.from({ length: limit + 1 }, (_, i) => ({
    label: String(new Date().getFullYear() - i),
    value: String(new Date().getFullYear() - i),
  }))

export const formatDateValue = (date: Date) => {
  // `fr-CA` use the `YYYY-MM-DD` format
  return date.toLocaleDateString('fr-CA')
}

export const isUKLicenceType = (licenceType: LicenceType) => {
  return (
    licenceType === LicenceType.UK ||
    licenceType === LicenceType.UK_FULL ||
    licenceType === LicenceType.UK_AUTO ||
    licenceType === LicenceType.UK_PROVISIONAL
  )
}

export const locations = [
  {
    label: 'In a carport',
    value: OvernightLocation.CARPORT,
  },
  {
    label: 'In an unlocked compound',
    value: OvernightLocation.UNLOCKED_COMPOUND,
  },
  {
    label: 'In an unlocked garage',
    value: OvernightLocation.UNLOCKED_GARAGE,
  },
  {
    label: 'On the road at home',
    value: OvernightLocation.ON_THE_ROAD_AT_HOME,
  },
  {
    label: 'On the road away from home',
    value: OvernightLocation.ON_THE_ROAD_AWAY_FROM_HOME,
  },
  {
    label: 'On a private driveway',
    value: OvernightLocation.PRIVATE_DRIVEWAY,
  },
  {
    label: 'In a work car park',
    value: OvernightLocation.OFFICE_OR_FACTORY_CAR_PARK,
  },
  {
    label: 'In an open public car park',
    value: OvernightLocation.OPEN_PUBLIC_CAR_PARK,
  },
  {
    label: 'In an secure public car park',
    value: OvernightLocation.SECURE_PUBLIC_CAR_PARK,
  },
  {
    label: 'In a locked garage',
    value: OvernightLocation.LOCKED_GARAGE,
  },
  {
    label: 'In a locked compound',
    value: OvernightLocation.LOCKED_COMPOUND,
  },
]

export const generateYearsInUkList = (dateOfBirth: DateObject | null): OptionItem[] => {
  const currentDate = convertToUkDate(new Date())
  const currentYear = currentDate.getFullYear()
  // If no current date selected, default to 17 (minimum age to drive)
  let dateOfBirthYear = currentYear - 17

  if (isCompleteDateObjectType(dateOfBirth)) {
    const dateOfBirthDate = convertDateObjectIntoDate(dateOfBirth)
    if (isPast(dateOfBirthDate)) {
      const { years } = intervalToDuration({
        start: convertToUkDate(dateOfBirthDate),
        end: currentDate,
      })
      if (years) {
        dateOfBirthYear = currentYear - years
      }
    }
  }

  const yearsInUk = currentYear - dateOfBirthYear
  const yearsInUkList = [...Array(yearsInUk + 1).keys()].map((_, i) => {
    if (i === 0) return { label: 'Less than 1 year', value: '0' }
    return { label: `${i} years`, value: String(i) }
  })

  // Add from birth option
  yearsInUkList.unshift({
    label: 'From birth',
    value: 'Since birth',
  })

  return yearsInUkList
}

export const licenceTypeList: OptionItem[] = [
  {
    label: 'Full UK licence',
    value: LicenceType.UK_FULL,
  },
  {
    label: 'Full UK licence (automatic)',
    value: LicenceType.UK_AUTO,
  },
  {
    label: 'Provisional UK licence',
    value: LicenceType.UK_PROVISIONAL,
  },
  {
    label: 'Full European licence (EU)',
    value: LicenceType.EUROPEAN,
  },
  {
    label: 'Full European licence (non-EU)',
    value: LicenceType.OTHER_EUROPEAN,
  },
  {
    label: 'Full licence from another country',
    value: LicenceType.INTERNATIONAL,
  },
]

export const vehicleSteeringOptions: OptionItem[] = [
  { label: 'Left', value: VehicleSteering.LHD },
  { label: 'Right', value: VehicleSteering.RHD },
]

/**
 * We use string validation but strict enum typing for fields like transmission and fuel type.
 * Since our enums may not cover all possible values, we validate as strings but log warnings
 * when we encounter values missing from our enums - indicating where we need to expand them.
 */
export const softEnum = <T extends string>(enumObj: { [key: string]: T }, fieldName: string) => {
  return string()
    .transform((value) => value as T)
    .test(`soft-enum-${fieldName}`, (value) => {
      if (!Object.values(enumObj).includes(value as T)) {
        datadogLogs.logger.warn(
          `${fieldName}: ${value} is not accounted for in our enum, please add it`,
        )
      }
      return true
    })
}

export const yearsSinceCurrentDate = (year: string, month: string): number => {
  const monthNumber = generateMonthNumber(month)

  if (!monthNumber) {
    return 0
  }

  const givenDate = new Date(Number(year), monthNumber - 1)
  const now = new Date()

  const yearsDiff = now.getFullYear() - givenDate.getFullYear()
  const monthsDiff = now.getMonth() - givenDate.getMonth()

  const totalMonths = yearsDiff * 12 + monthsDiff

  return Math.floor(totalMonths / 12)
}
